import { pb } from "./pocketbase";
import { financeiroIntegracaoService } from "./financeiroIntegracaoService";

export const suppliesService = {
  // --- 1. BUSCAR TODOS OS INSUMOS ---
  async getSupplies() {
    // Retorna todos os insumos ordenados por nome para a tabela
    return await pb.collection("supplies").getFullList({
      sort: "+name",
    });
  },

  // --- 2. CRIAR NOVO INSUMO ---
  async createSupply(supplyData) {
    return await pb.collection("supplies").create(supplyData);
  },

  // --- 3. ATUALIZAR INSUMO ---
  async updateSupply(id, supplyData) {
    return await pb.collection("supplies").update(id, supplyData);
  },

  // --- 4. EXCLUIR INSUMO ---
  async deleteSupply(id) {
    const linkedBoms = await pb.collection("sku_bom").getList(1, 1, {
      filter: `supply_id = "${id}"`
    });
    if (linkedBoms.totalItems > 0) {
      throw new Error("Este insumo não pode ser excluído porque está vinculado a receitas BOM da Matriz.");
    }
    return await pb.collection("supplies").delete(id);
  },

  // --- 5. ATUALIZAR ESTOQUE FÍSICO (Para quando mercadoria chegar) ---
  async adjustCurrentQuantity(id, delta) {
    // Atomic update para garantir prevencao de race condition na chegada/consumo
    // PocketBase tem operacoes unarias "+" ou "-" via API.
    return await pb.collection("supplies").update(id, {
      "current_quantity+": delta,
    });
  },

  // --- 6. ATUALIZAR ESTOQUE FÍSICO DIRETAMENTE (Reconciliação / Conferência) ---
  async setSupplyQuantity(id, quantity) {
    return await pb.collection("supplies").update(id, {
      current_quantity: quantity,
    });
  },

  // --- 7. BUSCAR CONFERÊNCIA PENDENTE POR SETOR ---
  async getPendingConference(sector) {
    try {
      const records = await pb.collection("supply_conferences").getList(1, 1, {
        filter: `status = "pending" && sector_filter = "${sector.toLowerCase()}"`,
        sort: "-created",
      });
      return records.items[0] || null;
    } catch (err) {
      console.error("Erro ao buscar rascunho de conferência:", err);
      return null;
    }
  },

  // --- 8. SALVAR CONFERÊNCIA (NOVA OU EXISTENTE) ---
  async saveConference(id, data) {
    if (id) {
      return await pb.collection("supply_conferences").update(id, data);
    } else {
      return await pb.collection("supply_conferences").create(data);
    }
  },

  // --- 9. BUSCAR PEDIDOS DE COMPRA ---
  async getPurchases() {
    return await pb.collection("supply_purchases").getFullList({
      sort: "-created",
    });
  },

  // --- 10. SALVAR PEDIDO DE COMPRA (NOVO OU EXISTENTE) ---
  async savePurchase(id, data) {
    if (id) {
      let oldRecord = null;
      try {
        oldRecord = await pb.collection("supply_purchases").getOne(id);
      } catch (err) {
        console.warn("Erro ao buscar rascunho de compra original:", err);
      }

      const res = await pb.collection("supply_purchases").update(id, data);

      const prevStatus = oldRecord?.status || "draft";
      const nextStatus = res.status || "draft";

      if (prevStatus === "draft" && (nextStatus === "ordered" || nextStatus === "received")) {
        try {
          const items = res.items || [];
          let totalCentavos = 0;
          for (const it of items) {
            const qty = it.qty_ordered || it.quantity || 0;
            const cost = it.custo_unitario || it.custo || it.price || it.cost || 0;
            totalCentavos += Math.round(qty * cost * 100);
          }

          await financeiroIntegracaoService.triggerPendingTransaction({
            origem: "supply_purchases",
            origemId: res.id,
            snapshot: res,
            dataSugerida: new Date().toISOString().split("T")[0],
            valorSugeridoCentavos: totalCentavos,
            descricaoSugerida: `Compra de Insumo - Lote ${res.id}`,
            tipoPendencia: "estorno",
          });
        } catch (errFin) {
          console.warn("Erro ao registrar pendência financeira da compra:", errFin);
        }
      }

      if (nextStatus === "cancelled" && prevStatus !== "cancelled") {
        try {
          await financeiroIntegracaoService.triggerCancellation("supply_purchases", id, pb.authStore.model);
        } catch (errFin) {
          console.warn("Erro ao cancelar pendência financeira para compra cancelada:", errFin);
        }
      }

      return res;
    } else {
      const res = await pb.collection("supply_purchases").create(data);

      if (res.status === "pending" || res.status === "ordered" || res.status === "received") {
        try {
          const items = res.items || [];
          let totalCentavos = 0;
          for (const it of items) {
            const qty = it.qty_ordered || it.quantity || 0;
            const cost = it.custo_unitario || it.custo || it.price || it.cost || 0;
            totalCentavos += Math.round(qty * cost * 100);
          }

          await financeiroIntegracaoService.triggerPendingTransaction({
            origem: "supply_purchases",
            origemId: res.id,
            snapshot: res,
            dataSugerida: new Date().toISOString().split("T")[0],
            valorSugeridoCentavos: totalCentavos,
            descricaoSugerida: `Compra de Insumo - Lote ${res.id}`,
            tipoPendencia: "estorno",
          });
        } catch (errFin) {
          console.warn("Erro ao registrar pendência financeira da compra:", errFin);
        }
      }

      return res;
    }
  },

  // --- 11. EXCLUIR PEDIDO DE COMPRA ---
  async deletePurchase(id) {
    try {
      await financeiroIntegracaoService.triggerCancellation("supply_purchases", id, pb.authStore.model);
    } catch (errFin) {
      console.warn("Erro ao disparar cancelamento para compra excluída:", errFin);
    }
    return await pb.collection("supply_purchases").delete(id);
  },

  // --- 12. BUSCAR COTAÇÃO DOS METAIS (AwesomeAPI com cache de 8h no cliente) ---
  async syncMetalQuotes() {
    const CACHE_KEY = "metal_quotes_cache";
    const CACHE_EXPIRY_MS = 8 * 60 * 60 * 1000; // 8 horas
    const now = Date.now();
    
    // 1. Tenta carregar do localStorage (cache local)
    if (typeof window !== "undefined") {
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { timestamp, data } = JSON.parse(cached);
          // O cache só é válido se não expirou e se já contém ouro, prata e dólar
          if (now - timestamp < CACHE_EXPIRY_MS && data && data.gold && data.silver && data.usd) {
            console.log("Cotações obtidas do cache local (expira em " + Math.round((CACHE_EXPIRY_MS - (now - timestamp)) / 60000) + " minutos)");
            return data;
          } else {
            console.log("Cache local expirado ou incompleto (sem ouro, prata ou dólar). Ignorando cache local.");
          }
        }
      } catch (e) {
        console.warn("Falha ao ler cache local de cotações:", e);
      }
    }

    // 2. Se expirou ou não existe cache, busca da AwesomeAPI
    try {
      console.log("Buscando cotações atualizadas da AwesomeAPI...");
      const response = await fetch("https://economia.awesomeapi.com.br/json/last/XAU-BRL,XAG-BRL,USD-BRL");
      if (!response.ok) throw new Error("Erro ao consultar API de cotações");
      const data = await response.json();
      
      if (!data.XAUBRL || !data.XAGBRL || !data.USDBRL) throw new Error("Format de resposta de cotações inválido");
      
      const goldPriceOz = Number(data.XAUBRL.bid);
      const silverPriceOz = Number(data.XAGBRL.bid);
      const usdPrice = Number(data.USDBRL.bid);
      
      const goldPriceGram = Math.round((goldPriceOz / 31.1034768) * 100) / 100;
      const silverPriceGram = Math.round((silverPriceOz / 31.1034768) * 100) / 100;
      
      const result = {
        gold: { price: goldPriceGram, pctChange: data.XAUBRL.pctChange },
        silver: { price: silverPriceGram, pctChange: data.XAGBRL.pctChange },
        usd: { price: usdPrice, pctChange: data.USDBRL.pctChange }
      };

      console.log("Cotações obtidas com sucesso da API:", result);

      // Salva no cache
      if (typeof window !== "undefined") {
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            timestamp: now,
            data: result
          }));
        } catch (e) {
          console.warn("Falha ao salvar cache de cotações:", e);
        }
      }

      return result;
    } catch (err) {
      console.error("Falha ao obter cotação de metais da API:", err);
      
      // Fallback 1: Retorna cache expirado se houver
      if (typeof window !== "undefined") {
        try {
          const cached = localStorage.getItem(CACHE_KEY);
          if (cached) {
            const { data } = JSON.parse(cached);
            if (data && data.gold && data.silver && data.usd) {
              console.log("Retornando cotações expiradas como fallback devido a falha da API");
              return data;
            }
          }
        } catch (e) {}
      }

      // Fallback 2: Valores estáticos em último caso
      console.log("Usando valores estáticos como fallback final para cotações");
      return {
        gold: { price: 720.50, pctChange: "0.15" },
        silver: { price: 11.32, pctChange: "-0.22" },
        usd: { price: 5.36, pctChange: "0" }
      };
    }
  },

  // --- 13. OBTER CUSTO UNITÁRIO DE INSUMO (Com conversão de dólar para zircônias) ---
  getSupplyUnitCost(supply) {
    if (!supply) return 0;
    
    const isZirconia = supply.sector === "pedras" || 
      (supply.category && ["zircônia", "zirconia", "zircônias", "zirconias"].includes(supply.category.toLowerCase().trim()));
      
    if (isZirconia) {
      let props = supply.stone_properties;
      if (typeof props === "string" && props.trim() !== "") {
        try { props = JSON.parse(props); } catch (e) {}
      }
      const usdCost = Number(props?.custo) || 0;
      
      // Obtém cotação do dólar do localStorage
      let dolar = 5.36;
      if (typeof window !== "undefined") {
        try {
          const cached = localStorage.getItem("metal_quotes_cache");
          if (cached) {
            const parsed = JSON.parse(cached);
            if (parsed && parsed.data && parsed.data.usd && parsed.data.usd.price) {
              dolar = Number(parsed.data.usd.price);
            }
          }
        } catch (e) {}
      }
      return usdCost * dolar;
    }
    
    // Comportamento padrão para outros insumos
    const compCost = Number(supply.custo_unidade_compra) || 0;
    const compQty = Number(supply.unidades_por_compra) || 1;
    return compCost > 0 ? (compCost / compQty) : (Number(supply.custo_unitario) || 0);
  }
};
